import React, { useState } from "react";
import { Send, Phone, Mail, MapPin, Printer, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { FAQ_ITEMS } from "../seo/seoConfig";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", { ...form, source: "contact-page" });
      toast.success("Thank you — your enquiry has been received.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-[64px] bg-[#12201A] min-h-screen">
      <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-16 grid md:grid-cols-2 gap-14">
        <div>
          <p className="eyebrow text-[#C9B79C] mb-3">Request Information</p>
          <h1 className="font-display font-light text-4xl md:text-5xl text-[#F3EEE3]">Speak with our land team</h1>
          <p className="text-[#F3EEE3]/70 mt-5 leading-relaxed">
            Enquire about available parcels, pricing, servicing timelines, zoning and investment materials for Deer
            Park Estates.
          </p>
          <div className="mt-8 rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-6" data-testid="contact-details">
            <div className="flex items-center gap-2.5 text-[#C9B79C]">
              <Building2 size={17} />
              <p className="font-display text-xl text-[#F3EEE3]">Broadview Developments Inc.</p>
            </div>
            <p className="text-[#F3EEE3]/60 text-sm mt-1 ml-[27px]">Franz Leung — President</p>
            <div className="mt-5 space-y-3.5 text-[#F3EEE3]/80 text-sm">
              <p className="flex items-start gap-3" data-testid="contact-address">
                <MapPin size={16} className="text-[#C9B79C] mt-0.5 shrink-0" />
                <span>13912 - 85 Ave., Edmonton, Alberta, Canada T5R 3Y8</span>
              </p>
              <p className="flex items-center gap-3" data-testid="contact-phone">
                <Phone size={16} className="text-[#C9B79C] shrink-0" />
                <a href="tel:+17804458188" className="hover:text-[#F3EEE3] transition-colors">780-445-8188</a>
              </p>
              <p className="flex items-center gap-3" data-testid="contact-fax">
                <Printer size={16} className="text-[#C9B79C] shrink-0" />
                <span>Fax: 780-483-1424</span>
              </p>
              <p className="flex items-center gap-3" data-testid="contact-email">
                <Mail size={16} className="text-[#C9B79C] shrink-0" />
                <a href="mailto:franz.leung@gmail.com" className="hover:text-[#F3EEE3] transition-colors">franz.leung@gmail.com</a>
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-8 space-y-4">
          {["name", "email", "phone"].map((k) => (
            <input
              key={k}
              required={k !== "phone"}
              type={k === "email" ? "email" : "text"}
              value={form[k]}
              onChange={set(k)}
              placeholder={k[0].toUpperCase() + k.slice(1) + (k === "phone" ? " (optional)" : "")}
              data-testid={`contact-${k}`}
              className="w-full bg-[#0C1510] border border-[#F3EEE3]/15 rounded-lg px-4 py-3 text-[#F3EEE3] placeholder:text-[#F3EEE3]/35 focus:border-[#C9B79C] outline-none"
            />
          ))}
          <textarea value={form.message} onChange={set("message")} rows={4} placeholder="Your message" data-testid="contact-message" className="w-full bg-[#0C1510] border border-[#F3EEE3]/15 rounded-lg px-4 py-3 text-[#F3EEE3] placeholder:text-[#F3EEE3]/35 focus:border-[#C9B79C] outline-none" />
          <button type="submit" disabled={busy} data-testid="contact-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-6 py-3.5 rounded-full font-medium hover:bg-white transition-colors disabled:opacity-60">
            <Send size={16} /> {busy ? "Sending…" : "Send enquiry"}
          </button>
        </form>
      </div>

      {/* ------------------------------------------------------------ FAQ */}
      <section className="max-w-[900px] mx-auto px-5 md:px-8 pb-24" data-testid="faq-section">
        <p className="eyebrow text-[#C9B79C] mb-3">Frequently Asked</p>
        <h2 className="font-display font-light text-3xl md:text-4xl text-[#F3EEE3] mb-8">
          Questions from builders &amp; investors
        </h2>
        <Accordion type="single" collapsible className="w-full">
          {FAQ_ITEMS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="border-[#F3EEE3]/12"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="text-[#F3EEE3] text-base md:text-lg font-display font-normal hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#F3EEE3]/70 text-sm leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
};

export default Contact;
